import { Skeleton } from '@/components/ui/skeleton';
import { useAppTheme, useCreateExperience } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { TEMPLATES, templateDoc, type Template } from '@/templates';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

// Picking a template clones its JSON into a new draft bound to the app's theme, saves it, and drops
// the author straight into the builder. A "blank" option is the same thing with one empty step —
// the empty root a `createStep()` already produces.

const SURFACE_HINT: Record<string, string> = {
  modal: 'Modal',
  fullscreen: 'Fullscreen',
  banner: 'Banner',
  bottom_sheet: 'Bottom sheet',
};

export function TemplateGallery(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: theme, isLoading } = useAppTheme(appId);
  const create = useCreateExperience(appId);
  const navigate = useNavigate();

  const start = (template: Template): void => {
    create.mutate(
      { name: template.name, document: templateDoc(template, theme) },
      {
        onSuccess: (experience) => void navigate({ to: '/experiences/$id/edit', params: { id: experience.id } }),
        onError: (error: Error) => toast.error(error.message),
      }
    );
  };

  if (isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      {TEMPLATES.map((template) => (
        <button
          key={template.id}
          type="button"
          disabled={create.isPending}
          onClick={() => start(template)}
          className="flex flex-col gap-1 rounded-xl border p-4 text-left transition-colors hover:border-indigo-500 hover:bg-secondary/50 disabled:opacity-50"
        >
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium">{template.name}</span>
            <span className="rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {SURFACE_HINT[template.steps[0]!.surface] ?? template.steps[0]!.surface}
              {template.steps.length > 1 ? ` · ${template.steps.length} steps` : ''}
            </span>
          </span>
          <span className="text-xs text-muted-foreground">{template.description}</span>
        </button>
      ))}
    </div>
  );
}
